import { FieldMessage, useTranslate } from '@athena/ui'

import { EmailCodeForm } from './EmailCodeForm.js'
import { SocialSignIn } from './SocialSignIn.js'
import { providerErrorKey } from './providers.js'

export interface SignInPanelProps {
  /**
   * Where a provider should land the person, as an app path with no language
   * prefix. The code form has no equivalent because a code never leaves the
   * tab — see `EmailCodeForm`.
   */
  callbackPath?: string
  /** Defaults to the plain "email me a code". The invitation flow says more. */
  submitLabel?: string
  /** Called once there is a session, whichever way it was got. */
  onSignedIn: () => void
  /**
   * The `?error=` a provider round trip came back with, if any. Mapped to a
   * sentence here and never printed raw.
   */
  providerError?: string | undefined
}

/**
 * The three ways in, in one panel.
 *
 * ── The order ───────────────────────────────────────────────────────────
 *
 * Providers first, the code below. Not a preference: a person who has a Google
 * account signs in with one press and no context switch, and a person who does
 * not is going to read the whole panel anyway. The divider is what says the two
 * halves are alternatives rather than steps — without it a form under two
 * buttons reads as "and then".
 *
 * ── The divider is unconditional, because the buttons are ───────────────
 *
 * `SocialSignIn` always renders both providers V1 supports and disables the
 * ones this deployment has no credentials for, so there is never an empty
 * section above the rule for it to label.
 */
export function SignInPanel({
  callbackPath = '/',
  submitLabel,
  onSignedIn,
  providerError,
}: SignInPanelProps) {
  const t = useTranslate()
  const errorKey = providerErrorKey(providerError)

  return (
    <div className="flex w-full flex-col gap-6">
      {errorKey ? (
        // `alert` rather than `status`: the person arrived here from a
        // provider expecting to be signed in, and the reason they are not is
        // the first thing they need to know.
        <FieldMessage tone="error" role="alert">
          {t(errorKey)}
        </FieldMessage>
      ) : null}

      <SocialSignIn callbackPath={callbackPath} />

      <EmailCodeForm {...(submitLabel ? { submitLabel } : {})} onSignedIn={onSignedIn} />
    </div>
  )
}
